/**
 * Obstruction API → Domain Mappers
 *
 * Convert API response payloads (ISO date strings, raw enum strings) into the
 * frontend domain types declared in `@/types/obstructions`. Mirrors the shape
 * of `noteMapper.ts` — pure functions, no React or store imports.
 */
import type {
  Obstruction,
  ObstructionListItem,
  ObstructionMapItem,
  ObstructionContractor,
  ObstructionGeometry,
  ObstructionGeometryMapItem,
  ObstructionGeoData,
  ObstructionSign,
  ObstructionAffectedRoad,
  ObstructionAttachment,
} from '@/types/obstructions';
import {
  ObstructionType,
  ObstructionStatus,
  SeverityLevel,
  TrafficImpact,
  ObstructionGeometryType,
} from '@/types/obstructions';
import type { Coordinates } from '@/types';
import type {
  ObstructionResponse,
  ObstructionListItemResponse,
  ObstructionMapResponse,
  ObstructionContractorResponse,
  ObstructionGeometryResponse,
  ObstructionGeometryMapResponse,
  ObstructionSignResponse,
  ObstructionAffectedRoadResponse,
  ObstructionAttachmentResponseDto,
} from '@/types/api/responses';

/**
 * Cast the raw GeoJSON-ish payload returned by the backend into the typed
 * `ObstructionGeoData` shape. The payload is opaque at this Wave 1 stage —
 * keep the cast localised so a future schema change touches only this file.
 */
export function mapObstructionGeoData(raw: unknown): ObstructionGeoData {
  return raw as ObstructionGeoData;
}

/** Copy a `{ lat, lng }` pair onto a fresh object — avoids leaking response refs. */
function mapCoordinates(point: { lat: number; lng: number }): Coordinates {
  return { lat: point.lat, lng: point.lng };
}

/**
 * Map the contractor block. Date-only strings (`YYYY-MM-DD`) are kept as
 * strings — UI layers format them via `useLocalizedDate` parsers.
 */
function mapObstructionContractorFromApi(
  response: ObstructionContractorResponse
): ObstructionContractor {
  return {
    company: response.company,
    manager: response.manager,
    phone: response.phone,
    email: response.email,
    emergencyContact: response.emergencyContact,
    permitNumber: response.permitNumber,
    permitIssuedDate: response.permitIssuedDate,
    permitExpiryDate: response.permitExpiryDate,
    notes: response.notes,
  };
}

/**
 * Map a full geometry payload to the frontend domain shape.
 */
function mapObstructionGeometryFromApi(
  response: ObstructionGeometryResponse
): ObstructionGeometry {
  return {
    id: response.id,
    obstructionId: response.obstructionId,
    type: response.type as ObstructionGeometryType,
    name: response.name,
    description: response.description,
    geoData: mapObstructionGeoData(response.geoData),
    calculatedCenter: mapCoordinates(response.calculatedCenter),
    sortOrder: response.sortOrder,
    createdAt: new Date(response.createdAt),
    updatedAt: response.updatedAt ? new Date(response.updatedAt) : null,
  };
}

/**
 * Map the minimal map-optimised geometry payload.
 */
function mapObstructionGeometryMapItemFromApi(
  response: ObstructionGeometryMapResponse
): ObstructionGeometryMapItem {
  return {
    id: response.id,
    type: response.type as ObstructionGeometryType,
    calculatedCenter: mapCoordinates(response.calculatedCenter),
    geoData: mapObstructionGeoData(response.geoData),
  };
}

/**
 * Map an obstruction sign payload.
 */
function mapObstructionSignFromApi(
  response: ObstructionSignResponse
): ObstructionSign {
  return {
    id: response.id,
    obstructionId: response.obstructionId,
    geometryId: response.geometryId,
    signCode: response.signCode,
    note: response.note,
    placementPoint: mapCoordinates(response.placementPoint),
    createdAt: new Date(response.createdAt),
  };
}

/**
 * Map an obstruction affected-road payload (detail-only collection).
 */
function mapObstructionAffectedRoadFromApi(
  response: ObstructionAffectedRoadResponse
): ObstructionAffectedRoad {
  return {
    id: response.id,
    obstructionId: response.obstructionId,
    location: response.location,
    description: response.description,
    createdAt: new Date(response.createdAt),
  };
}

/**
 * Map an obstruction attachment payload (detail-only collection). Field names
 * are normalised to the source-agnostic domain shape
 * (`name` / `type` / `size`).
 */
function mapObstructionAttachmentFromApi(
  response: ObstructionAttachmentResponseDto
): ObstructionAttachment {
  return {
    id: response.id,
    obstructionId: response.obstructionId,
    name: response.fileName,
    type: response.contentType,
    size: response.fileSize,
    storageKey: response.storageKey,
    uploadedBy: response.uploadedBy,
    uploadedAt: new Date(response.uploadedAt),
  };
}

/**
 * Map an obstruction API response to a frontend `Obstruction`.
 */
export function mapObstructionFromApi(
  response: ObstructionResponse
): Obstruction {
  return {
    id: response.id,
    shortId: response.shortId,
    workspaceId: response.workspaceId,
    title: response.title,
    description: response.description,
    type: response.type as ObstructionType,
    status: response.status as ObstructionStatus,
    severity: response.severity as SeverityLevel,
    impact: response.impact as TrafficImpact,
    location: response.location,
    internalNotes: response.internalNotes,
    plannedStartDate: new Date(response.plannedStartDate),
    plannedEndDate: new Date(response.plannedEndDate),
    actualStartDate: response.actualStartDate
      ? new Date(response.actualStartDate)
      : null,
    actualEndDate: response.actualEndDate
      ? new Date(response.actualEndDate)
      : null,
    contractor: mapObstructionContractorFromApi(response.contractor),
    anchorPoint: mapCoordinates(response.anchorPoint),
    geometries: response.geometries.map(mapObstructionGeometryFromApi),
    signs: response.signs.map(mapObstructionSignFromApi),
    affectedRoads: response.affectedRoads.map(
      mapObstructionAffectedRoadFromApi
    ),
    attachments: (response.attachments ?? []).map(
      mapObstructionAttachmentFromApi
    ),
    createdBy: response.createdBy,
    creatorName: response.creatorName,
    creatorAvatarUrl: response.creatorAvatarUrl,
    creatorIsFormerMember: response.creatorIsFormerMember,
    createdAt: new Date(response.createdAt),
    updatedAt: response.updatedAt ? new Date(response.updatedAt) : null,
  };
}

/**
 * Map a lightweight list-item API response.
 */
export function mapObstructionListItemFromApi(
  response: ObstructionListItemResponse
): ObstructionListItem {
  return {
    id: response.id,
    shortId: response.shortId,
    title: response.title,
    type: response.type as ObstructionType,
    status: response.status as ObstructionStatus,
    severity: response.severity as SeverityLevel,
    impact: response.impact as TrafficImpact,
    location: response.location,
    contractorCompany: response.contractorCompany,
    plannedStartDate: new Date(response.plannedStartDate),
    plannedEndDate: new Date(response.plannedEndDate),
    actualStartDate: response.actualStartDate
      ? new Date(response.actualStartDate)
      : null,
    actualEndDate: response.actualEndDate
      ? new Date(response.actualEndDate)
      : null,
    geometryCount: response.geometryCount,
    signCount: response.signCount,
    createdAt: new Date(response.createdAt),
    updatedAt: response.updatedAt ? new Date(response.updatedAt) : null,
  };
}

/**
 * Map a map-optimised obstruction API response.
 */
export function mapObstructionMapItemFromApi(
  response: ObstructionMapResponse
): ObstructionMapItem {
  return {
    id: response.id,
    shortId: response.shortId,
    title: response.title,
    type: response.type as ObstructionType,
    status: response.status as ObstructionStatus,
    severity: response.severity as SeverityLevel,
    impact: response.impact as TrafficImpact,
    anchorPoint: mapCoordinates(response.anchorPoint),
    geometries: response.geometries.map(mapObstructionGeometryMapItemFromApi),
  };
}

/**
 * Convert a full `Obstruction` domain object to its lightweight list-item
 * shape. Mirrors `noteToListItem` — used when a CRUD operation returns a
 * full obstruction but the list panel only stores list-item rows.
 */
export function obstructionToListItem(
  obstruction: Obstruction
): ObstructionListItem {
  return {
    id: obstruction.id,
    shortId: obstruction.shortId,
    title: obstruction.title,
    type: obstruction.type,
    status: obstruction.status,
    severity: obstruction.severity,
    impact: obstruction.impact,
    location: obstruction.location,
    contractorCompany: obstruction.contractor.company,
    plannedStartDate: obstruction.plannedStartDate,
    plannedEndDate: obstruction.plannedEndDate,
    actualStartDate: obstruction.actualStartDate,
    actualEndDate: obstruction.actualEndDate,
    geometryCount: obstruction.geometries.length,
    signCount: obstruction.signs.length,
    createdAt: obstruction.createdAt,
    updatedAt: obstruction.updatedAt,
  };
}
